/**
 * API client pour communiquer avec le backend Django
 */

const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV
  ? '/api'
  : 'https://profile-sparkle-magic-production.up.railway.app/api');

// Le cache prerendu expire : le navigateur doit pouvoir relire la meme API.
export const USE_API = !!API_URL;

export interface BlogPost {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  author: string;
  category: string;
  tags: string[];
  cover_image: string;
  reading_time: number;
  featured: boolean;
  published_at: string;
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: number;
  name: string;
  slug: string;
  description: string;
}

export interface Tag {
  id: number;
  name: string;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

/**
 * Récupère TOUTES les pages d'un endpoint DRF paginé en suivant `next`.
 * Gère aussi les endpoints qui renvoient un tableau brut (ex: /posts/featured/).
 * Les liens `next` reviennent en http://, ce qui casse en mixed-content sur une
 * page https : on réécrit donc le schéma en https. Le garde-fou `guard` évite
 * toute boucle infinie si le backend renvoyait un `next` qui pointe sur lui-même.
 */
/**
 * Suit la pagination DRF jusqu'au bout.
 *
 * On demande d'emblee la plus grande page que le backend accepte (100). Sans
 * ce parametre, ramener 35 articles exigeait QUATRE allers-retours
 * sequentiels jusqu'a Railway, et c'est ce qui faisait de /blog la page la
 * plus lente a atteindre. La boucle reste la, en filet : si un jour il y a
 * plus de 100 articles, ou si un backend plus ancien ignore le parametre,
 * elle enchaine les pages comme avant.
 */
export async function fetchAllPages<T>(firstUrl: string): Promise<T[]> {
  const all: T[] = [];
  const separateur = firstUrl.includes("?") ? "&" : "?";
  let url: string | null = `${firstUrl}${separateur}page_size=100`;
  let guard = 0;
  while (url && guard < 100) {
    guard++;
    const response = await fetch(url);
    if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`);
    const data = await response.json();
    if (Array.isArray(data)) {
      all.push(...data);
      break;
    }
    all.push(...(data.results || []));
    url = data.next ? data.next.replace(/^http:\/\//, 'https://') : null;
  }
  return all;
}

// Fetch tous les articles (toutes les pages)
export async function fetchPosts(): Promise<BlogPost[]> {
  return fetchAllPages<BlogPost>(`${API_URL}/posts/`);
}

// Fetch un article par slug
export async function fetchPostBySlug(slug: string): Promise<BlogPost | null> {
  const response = await fetch(`${API_URL}/posts/${slug}/`);
  if (!response.ok) {
    if (response.status === 404) return null;
    throw new Error('Failed to fetch post');
  }
  return response.json();
}

// Fetch articles en vedette
export async function fetchFeaturedPosts(): Promise<BlogPost[]> {
  return fetchAllPages<BlogPost>(`${API_URL}/posts/featured/`);
}

// Fetch articles par catégorie (toutes les pages)
export async function fetchPostsByCategory(categorySlug: string): Promise<BlogPost[]> {
  return fetchAllPages<BlogPost>(`${API_URL}/posts/by_category/?category=${categorySlug}`);
}

// Fetch toutes les catégories (toutes les pages)
export async function fetchCategories(): Promise<Category[]> {
  return fetchAllPages<Category>(`${API_URL}/categories/`);
}

// Fetch tous les tags (toutes les pages)
export async function fetchTags(): Promise<Tag[]> {
  return fetchAllPages<Tag>(`${API_URL}/tags/`);
}

// Adapter le format Django vers le format attendu par les composants
export function adaptPost(post: BlogPost): { id: string; title: string; slug: string; excerpt: string; content: string; author: string; category: string; tags: string[]; coverImage: string; readingTime: number; featured: boolean; publishedAt: string; updatedAt?: string } {
  return {
    id: String(post.id),
    title: post.title,
    slug: post.slug,
    excerpt: post.excerpt,
    content: post.content,
    author: post.author,
    category: post.category,
    tags: post.tags,
    coverImage: post.cover_image,
    readingTime: post.reading_time,
    featured: post.featured,
    publishedAt: post.published_at,
    updatedAt: post.updated_at,
  };
}
